import React, { FC, useState, memo } from "react";
import { toastr } from "react-redux-toastr";
import { IAuthState } from "src/modules/Auth/store/types";
import {
  ReportTypes,
  ReportsService,
  CaseReportsOptions,
} from "src/share/services/reports";
import { Heatmap } from "src/share/components/Maps/Heatmap/Heatmap";
import { ChartHeader } from "./components/ChartHeader";
import ChartLoader from "src/share/components/ChartLoader/ChartLoader";
import YearChart from "./components/YearChart";
import { YearLoader } from "src/share/components/ChartLoader/YearLoader";
import MonthlyHighlights from "./components/MonthlyHighlights";
import CaseTypeChart from "./components/CaseTypeChart";
import { SexLoader } from "src/share/components/ChartLoader/SexLoader";
import SexChart from "./components/SexChart";
import ChartBars from "src/share/components/Chart/ChartBars";
import { AbductorLoader } from "src/share/components/ChartLoader/AbductorLoader";
import DatesChart from "src/share/components/Chart/ChartDays";
import StatsFilters from "./components/StatsFilters";
import useStats from "./hooks/useStats";
import "./stats.css";

type Props = {
  auth: IAuthState;
};

const Stats: FC<Props> = ({ auth }) => {
  const { metrics, filters, isLoading, onApply } = useStats();
  const [selectedReport, setSelectedReport] = useState("");

  const getReport = (type) => async (): Promise<void> => {
    try {
      setSelectedReport(type);
      const request = {
        type: ReportTypes.CASES,
        report: type,
        agencyCode: filters.agency,
        state: filters.region,
      };
      await ReportsService.get(ReportTypes.CASES, type, request);
      setSelectedReport("");
    } catch (error) {
      setSelectedReport("");
      toastr.error("Error", "There was a problem getting the report");
    }
  };

  const {
    // caseMetrics,
    yearMetrics,
    sexMetrics,
    missingCity,
    missingCountry,
    caseType,
    casesByWeek,
  } = metrics;
  const countryCityData =
    filters.agency === "ALL" ? missingCountry : missingCity;
  return (
    <div className="stats">
      <StatsFilters onFiltersApply={onApply} />
      <div className="margin-heat-map">
        <div className="white-background">
          <Heatmap
            auth={auth}
            agencyCode={filters.agency}
            state={filters.region}
            caseType={filters.caseType}
            status={filters.status}
            range={filters.range}
          />
        </div>
      </div>
      <div className="cases-by-year">
        <div className="white-background">
          <ChartHeader
            text={"Cases by year "}
            id="casesByYear"
            action={getReport(CaseReportsOptions.CASES_BY_YEAR)}
            showDownloadReport={yearMetrics.length > 0}
            loading={selectedReport}
          />
          <ChartLoader
            chart={YearChart}
            loader={YearLoader}
            data={yearMetrics}
            loading={isLoading}
          />
        </div>
      </div>
      <MonthlyHighlights
        agencyCode={filters.agency}
        range={filters.range}
        status={filters.status}
        caseType={filters.caseType}
        loadingReport={selectedReport}
        onReportDownload={getReport(CaseReportsOptions.MONTHLY_HIGHLIGHTS)}
      />
      <div className="half-cards">
        <div className="white-background">
          <ChartHeader
            text={"Cases by type "}
            id="caseType"
            action={getReport(CaseReportsOptions.CASE_TYPE)}
            showDownloadReport={caseType.length > 0}
            loading={selectedReport}
          />
          <ChartLoader
            chart={CaseTypeChart}
            loader={SexLoader}
            data={caseType}
            loading={isLoading}
          />
        </div>
        <div className="white-background">
          <ChartHeader
            text={"Missing children by sex "}
            id="childrenSex"
            action={getReport(CaseReportsOptions.CHILDREN_SEX)}
            showDownloadReport={sexMetrics.length > 0}
            loading={selectedReport}
          />
          <ChartLoader
            chart={SexChart}
            loader={SexLoader}
            data={sexMetrics}
            loading={isLoading}
          />
        </div>
      </div>
      <div className="white-background">
        <ChartHeader
          text={
            filters.agency === "ALL"
              ? "Missing children by country "
              : "Missing children by city "
          }
          id="missingCity"
          action={
            filters.agency === "ALL"
              ? getReport(CaseReportsOptions.MISSING_COUNTRY)
              : getReport(CaseReportsOptions.MISSING_CITY)
          }
          showDownloadReport={countryCityData.length > 0}
          loading={selectedReport}
        />
        <ChartLoader
          chart={ChartBars}
          loader={AbductorLoader}
          data={countryCityData}
          loading={isLoading}
        />
      </div>
      <div className="white-background">
        <ChartHeader
          text={"Weekly cases "}
          id="weeklyCases"
          action={getReport(CaseReportsOptions.CASES_BY_WEEK)}
          showDownloadReport={casesByWeek.length > 0}
          loading={selectedReport}
        />
        <ChartLoader
          chart={DatesChart}
          dataKey={"cases"}
          loader={YearLoader}
          data={casesByWeek}
          loading={isLoading}
        />
      </div>
    </div>
  );
};

export default memo(Stats);
